import { useState, useEffect, useRef, useCallback } from 'react'
import { X } from 'lucide-react'
import { cn } from '../lib/utils'

const SNAP_POINTS = [0.35, 0.70, 0.95] // 35%, 70%, 95%
const CLOSE_THRESHOLD = 0.25 // Close if dragged below 25%

export function BottomSheet({ 
  isOpen, 
  onClose, 
  title, 
  children,
  initialSnap = 1 // 0 = 35%, 1 = 70%, 2 = 95%
}) {
  const [isVisible, setIsVisible] = useState(false)
  const [currentHeight, setCurrentHeight] = useState(SNAP_POINTS[initialSnap])
  const [isDragging, setIsDragging] = useState(false)
  const sheetRef = useRef(null)
  const dragStartY = useRef(0)
  const dragStartHeight = useRef(0)

  // Animation on mount
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        setIsVisible(true)
        setCurrentHeight(SNAP_POINTS[initialSnap])
      })
    }
  }, [isOpen, initialSnap])

  const handleClose = useCallback(() => {
    setIsVisible(false)
    setTimeout(onClose, 200)
  }, [onClose])

  const findNearestSnapPoint = (height) => {
    if (height < CLOSE_THRESHOLD) return null // Signal to close
    
    let nearest = SNAP_POINTS[0]
    let minDiff = Math.abs(height - SNAP_POINTS[0])
    
    for (const snap of SNAP_POINTS) {
      const diff = Math.abs(height - snap)
      if (diff < minDiff) {
        minDiff = diff
        nearest = snap
      }
    }
    return nearest
  }

  const handleDragStart = (e) => {
    setIsDragging(true)
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY
    dragStartY.current = clientY
    dragStartHeight.current = currentHeight
  }

  const handleDrag = useCallback((e) => {
    if (!isDragging) return
    
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY
    const deltaY = dragStartY.current - clientY
    const deltaPercent = deltaY / window.innerHeight
    const newHeight = Math.max(0.1, Math.min(0.95, dragStartHeight.current + deltaPercent))
    
    setCurrentHeight(newHeight)
  }, [isDragging])

  const handleDragEnd = useCallback(() => {
    if (!isDragging) return
    setIsDragging(false)
    
    const snapPoint = findNearestSnapPoint(currentHeight)
    if (snapPoint === null) {
      handleClose()
    } else {
      setCurrentHeight(snapPoint)
    }
  }, [isDragging, currentHeight, handleClose])

  // Add global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag)
      window.addEventListener('mouseup', handleDragEnd)
      window.addEventListener('touchmove', handleDrag, { passive: false })
      window.addEventListener('touchend', handleDragEnd)
    }
    
    return () => {
      window.removeEventListener('mousemove', handleDrag)
      window.removeEventListener('mouseup', handleDragEnd)
      window.removeEventListener('touchmove', handleDrag)
      window.removeEventListener('touchend', handleDragEnd)
    }
  }, [isDragging, handleDrag, handleDragEnd])

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/60 z-40 transition-opacity duration-200",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        onClick={handleClose}
      />
      
      {/* Bottom Sheet */}
      <div 
        ref={sheetRef}
        className={cn(
          "fixed left-0 right-0 bottom-0 z-50 bg-background rounded-t-3xl shadow-2xl",
          isDragging ? "" : "transition-all duration-300 ease-out",
          isVisible ? "translate-y-0" : "translate-y-full"
        )}
        style={{ 
          height: `${currentHeight * 100}vh`,
          maxHeight: '95vh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        {/* Drag Handle */}
        <div 
          className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className="w-12 h-1.5 rounded-full bg-muted-foreground/40" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pb-3 border-b">
          <h2 className="text-lg font-bold">{title}</h2>
          <button 
            onClick={handleClose} 
            className="p-2 -mr-2 rounded-full hover:bg-secondary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div 
          className="overflow-y-auto overscroll-contain"
          style={{ height: 'calc(100% - 70px)' }}
        >
          {children}
        </div>
      </div>
    </>
  )
}

// Picker Sheet for nested selections (categories, currencies, etc.)
export function PickerSheet({ title, isOpen, onClose, children }) {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => setIsVisible(true))
    }
  }, [isOpen])

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(onClose, 200)
  }

  if (!isOpen) return null

  return (
    <>
      <div 
        className={cn(
          "fixed inset-0 bg-black/40 z-[60] transition-opacity duration-200",
          isVisible ? "opacity-100" : "opacity-0"
        )}
        onClick={handleClose}
      />
      <div 
        className={cn(
          "fixed left-0 right-0 bottom-0 z-[70] bg-background rounded-t-3xl transition-transform duration-200 ease-out shadow-2xl",
          isVisible ? "translate-y-0" : "translate-y-full"
        )}
        style={{ 
          maxHeight: '70vh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 border-b">
          <h3 className="font-bold">{title}</h3>
          <button onClick={handleClose} className="p-2 -mr-2 rounded-full hover:bg-secondary">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 80px)' }}>
          {children}
        </div>
      </div>
    </>
  )
}
